import {
  memo, useEffect, useState,
} from '../../lib/teact/teact';

import buildClassName from '../../util/buildClassName';

import useLastCallback from '../../hooks/useLastCallback';

import Icon from '../common/icons/Icon';

import styles from './ScheduleEditorModal.module.scss';

export interface DaySchedule {
  active: boolean;
  start: string;
  end: string;
}

export interface HolidayItem {
  id: string;
  name: string;
  date: string;
  recurring?: boolean;
  type: 'nacional' | 'regional';
  active: boolean;
}

export interface BusinessHoursData {
  enabled: boolean;
  schedule_type?: string;
  timezone: string;
  assistant_mode: string;
  auto_reply_outside?: boolean;
  auto_reply_holidays?: boolean;
  days: Record<string, DaySchedule>;
  holidays: HolidayItem[];
  notice_text?: string;
}

type OwnProps = {
  isOpen: boolean;
  initialData?: BusinessHoursData;
  isSaving?: boolean;
  onClose: NoneToVoidFunction;
  onSave: (payload: BusinessHoursData) => void;
};

const DAY_KEYS = [
  { key: 'monday', label: 'Lunes' },
  { key: 'tuesday', label: 'Martes' },
  { key: 'wednesday', label: 'Miércoles' },
  { key: 'thursday', label: 'Jueves' },
  { key: 'friday', label: 'Viernes' },
  { key: 'saturday', label: 'Sábado' },
  { key: 'sunday', label: 'Domingo' },
];

const DEFAULT_DAYS: Record<string, DaySchedule> = {
  monday: { active: true, start: '08:00', end: '16:00' },
  tuesday: { active: true, start: '08:00', end: '16:00' },
  wednesday: { active: true, start: '08:00', end: '16:00' },
  thursday: { active: true, start: '08:00', end: '16:00' },
  friday: { active: true, start: '08:00', end: '16:00' },
  saturday: { active: false, start: '08:00', end: '12:00' },
  sunday: { active: false, start: '08:00', end: '12:00' },
};

const ScheduleEditorModal = ({
  isOpen,
  initialData,
  isSaving,
  onClose,
  onSave,
}: OwnProps) => {
  const [activeTab, setActiveTab] = useState<'schedule' | 'holidays'>('schedule');
  const [days, setDays] = useState<Record<string, DaySchedule>>(DEFAULT_DAYS);
  const [timezone, setTimezone] = useState('America/La_Paz');
  const [assistantMode, setAssistantMode] = useState('human_supervision');
  const [noticeText, setNoticeText] = useState('');
  const [holidays, setHolidays] = useState<HolidayItem[]>([]);
  const [holidayFilter, setHolidayFilter] = useState<'all' | 'nacional' | 'regional'>('all');

  // New holiday form
  const [newHolidayName, setNewHolidayName] = useState('');
  const [newHolidayDate, setNewHolidayDate] = useState('');
  const [newHolidayType, setNewHolidayType] = useState<'nacional' | 'regional'>('nacional');

  useEffect(() => {
    if (!isOpen) return;
    if (initialData) {
      setDays(initialData.days || DEFAULT_DAYS);
      setTimezone(initialData.timezone || 'America/La_Paz');
      setAssistantMode(initialData.assistant_mode || 'human_supervision');
      setNoticeText(initialData.notice_text || '');
      setHolidays(initialData.holidays || []);
    }
  }, [isOpen, initialData]);

  const handleToggleDay = useLastCallback((key: string) => {
    setDays((prev) => {
      const current = prev[key] || { active: false, start: '08:00', end: '16:00' };
      return {
        ...prev,
        [key]: {
          ...current,
          active: !current.active,
        },
      };
    });
  });

  const handleTimeChange = useLastCallback((key: string, field: 'start' | 'end', val: string) => {
    setDays((prev) => {
      const current = prev[key] || { active: true, start: '08:00', end: '16:00' };
      return {
        ...prev,
        [key]: {
          ...current,
          [field]: val,
        },
      };
    });
  });

  const handleToggleHoliday = useLastCallback((id: string) => {
    setHolidays((prev) => prev.map((h) => (h.id === id ? { ...h, active: !h.active } : h)));
  });

  const handleDeleteHoliday = useLastCallback((id: string) => {
    setHolidays((prev) => prev.filter((h) => h.id !== id));
  });

  const handleAddHoliday = useLastCallback(() => {
    if (!newHolidayName.trim() || !newHolidayDate.trim()) return;
    const newId = `custom_${Date.now()}`;
    setHolidays((prev) => [
      ...prev,
      {
        id: newId,
        name: newHolidayName.trim(),
        date: newHolidayDate.trim(),
        type: newHolidayType,
        active: true,
        recurring: true,
      },
    ]);
    setNewHolidayName('');
    setNewHolidayDate('');
  });

  const handleSave = useLastCallback(() => {
    onSave({
      enabled: true,
      schedule_type: 'custom',
      timezone,
      assistant_mode: assistantMode,
      auto_reply_outside: assistantMode === 'auto_reply',
      auto_reply_holidays: true,
      days,
      holidays,
      notice_text: noticeText,
    });
  });

  if (!isOpen) return undefined;

  const filteredHolidays = holidays.filter((h) => {
    if (holidayFilter === 'all') return true;
    return h.type === holidayFilter;
  });

  return (
    <div className={styles.backdrop} onClick={onClose}>
      <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className={styles.header}>
          <div className={styles.headerInfo}>
            <div className={styles.headerIcon}>
              <Icon name="clock" />
            </div>
            <div>
              <h3 className={styles.headerTitle}>Editar Horarios de Atención y Feriados</h3>
              <p className={styles.headerSubtitle}>
                Configuración de franja horaria laboral de la Facultad y gestión de feriados nacionales/regionales.
              </p>
            </div>
          </div>
          <button
            type="button"
            className={styles.closeButton}
            onClick={onClose}
            aria-label="Cerrar"
          >
            <Icon name="close" />
          </button>
        </div>

        {/* Navigation Tabs */}
        <div className={styles.tabsBar}>
          <button
            type="button"
            className={buildClassName(styles.tabItem, activeTab === 'schedule' && styles.tabItemActive)}
            onClick={() => setActiveTab('schedule')}
          >
            🕒 Horarios Semanales
          </button>
          <button
            type="button"
            className={buildClassName(styles.tabItem, activeTab === 'holidays' && styles.tabItemActive)}
            onClick={() => setActiveTab('holidays')}
          >
            🗓️ Calendario de Feriados (
            {holidays.filter((h) => h.active).length}
            {' '}
            activos)
          </button>
        </div>

        {/* Body */}
        <div className={styles.body}>
          {activeTab === 'schedule' ? (
            <>
              {/* Tabla de Días Semanales */}
              <div>
                <h4 className={styles.sectionTitle}>Franjas de Atención por Día</h4>
                <div className={styles.daysTable}>
                  {DAY_KEYS.map(({ key, label }) => {
                    const dayConf = days[key] || { active: false, start: '08:00', end: '16:00' };
                    return (
                      <div
                        key={key}
                        className={buildClassName(styles.dayRow, !dayConf.active && styles.dayRowInactive)}
                      >
                        <label className={styles.dayCheckLabel}>
                          <input
                            type="checkbox"
                            checked={dayConf.active}
                            onChange={() => handleToggleDay(key)}
                          />
                          <span>{label}</span>
                        </label>

                        <div className={styles.timeInputs}>
                          <input
                            type="time"
                            className={styles.timeInput}
                            value={dayConf.start}
                            disabled={!dayConf.active}
                            onChange={(e) => handleTimeChange(key, 'start', e.target.value)}
                          />
                          <span className={styles.timeSeparator}>a</span>
                          <input
                            type="time"
                            className={styles.timeInput}
                            value={dayConf.end}
                            disabled={!dayConf.active}
                            onChange={(e) => handleTimeChange(key, 'end', e.target.value)}
                          />
                        </div>

                        <span
                          className={buildClassName(
                            styles.dayStatusBadge,
                            dayConf.active ? styles.statusActive : styles.statusInactive,
                          )}
                        >
                          {dayConf.active ? 'Continuo' : 'Inactivo'}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Zona Horaria */}
              <div className={styles.fieldGroup}>
                <span className={styles.label}>Zona Horaria Institucional</span>
                <select
                  className={styles.select}
                  value={timezone}
                  onChange={(e) => setTimezone(e.target.value)}
                >
                  <option value="America/La_Paz">América / La Paz (BOT - UTC-4)</option>
                  <option value="America/Lima">América / Lima (PET - UTC-5)</option>
                  <option value="America/Buenos_Aires">América / Buenos Aires (ART - UTC-3)</option>
                </select>
              </div>

              {/* Modo Asistente fuera de horario */}
              <div className={styles.fieldGroup}>
                <span className={styles.label}>Modo Asistente fuera de horario de oficina</span>
                <select
                  className={styles.select}
                  value={assistantMode}
                  onChange={(e) => setAssistantMode(e.target.value)}
                >
                  <option value="human_supervision">
                    Supervisión Humana (Human-in-the-loop - Respuestas con revisión previa)
                  </option>
                  <option value="auto_reply">
                    Auto-respuesta Automática Fuera de Horario (Aviso institucional inmediato)
                  </option>
                </select>
              </div>

              {/* Texto de Cierre Institucional (Canal Oficial) */}
              <div className={styles.fieldGroup}>
                <span className={styles.label}>Texto de Cierre al Final del Mensaje (Canal Oficial)</span>
                <textarea
                  className={styles.textarea}
                  rows={2}
                  value={noticeText}
                  onChange={(e) => setNoticeText(e.target.value)}
                  placeholder="Le invitamos a revisar nuestro Canal: https://t.me/PosgradoMedicinaUMSS"
                />
              </div>
            </>
          ) : (
            <>
              {/* Calendario de Feriados */}
              <div className={styles.holidaysBanner}>
                <span>💡</span>
                <span>
                  Feriados Regionales de Cochabamba incluidos:
                  {' '}
                  <strong>Virgen de Urkupiña (15 de Agosto)</strong>
                  {' '}
                  y
                  {' '}
                  <strong>Efeméride Departamental (14 de Septiembre)</strong>
                  .
                </span>
              </div>

              {/* Filtros */}
              <div className={styles.filterChips}>
                <button
                  type="button"
                  className={buildClassName(styles.filterChip, holidayFilter === 'all' && styles.filterChipActive)}
                  onClick={() => setHolidayFilter('all')}
                >
                  Todos (
                  {holidays.length}
                  )
                </button>
                <button
                  type="button"
                  className={buildClassName(styles.filterChip, holidayFilter === 'nacional' && styles.filterChipActive)}
                  onClick={() => setHolidayFilter('nacional')}
                >
                  Nacionales
                </button>
                <button
                  type="button"
                  className={buildClassName(styles.filterChip, holidayFilter === 'regional' && styles.filterChipActive)}
                  onClick={() => setHolidayFilter('regional')}
                >
                  Regionales Cochabamba
                </button>
              </div>

              {/* Lista de Feriados */}
              <div className={styles.holidaysList}>
                {filteredHolidays.map((holiday) => (
                  <div key={holiday.id} className={styles.holidayCard}>
                    <div className={styles.holidayLeft}>
                      <input
                        type="checkbox"
                        checked={holiday.active}
                        onChange={() => handleToggleHoliday(holiday.id)}
                      />
                      <div className={styles.holidayInfo}>
                        <span className={styles.holidayName}>{holiday.name}</span>
                        <span className={styles.holidayDate}>
                          Fecha:
                          {' '}
                          {holiday.date}
                        </span>
                      </div>
                    </div>

                    <div className={styles.holidayBadges}>
                      <span
                        className={holiday.type === 'regional' ? styles.badgeRegional : styles.badgeNacional}
                      >
                        {holiday.type === 'regional' ? 'Regional CBB' : 'Nacional'}
                      </span>
                      {holiday.id.startsWith('custom_') && (
                        <button
                          type="button"
                          className={styles.closeButton}
                          onClick={() => handleDeleteHoliday(holiday.id)}
                          aria-label="Eliminar"
                        >
                          <Icon name="close" />
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              {/* Agregar Feriado */}
              <div className={styles.addHolidayBox}>
                <div style="flex: 2;">
                  <span className={styles.label}>Nombre del Feriado</span>
                  <input
                    type="text"
                    className={styles.input}
                    value={newHolidayName}
                    onChange={(e) => setNewHolidayName(e.target.value)}
                    placeholder="Ej: Feriado Local San Simón"
                  />
                </div>
                <div style="flex: 1;">
                  <span className={styles.label}>Fecha (MM-DD)</span>
                  <input
                    type="text"
                    className={styles.input}
                    value={newHolidayDate}
                    onChange={(e) => setNewHolidayDate(e.target.value)}
                    placeholder="Ej: 09-14"
                  />
                </div>
                <div style="flex: 1;">
                  <span className={styles.label}>Ámbito</span>
                  <select
                    className={styles.select}
                    value={newHolidayType}
                    onChange={(e) => setNewHolidayType(e.target.value as 'nacional' | 'regional')}
                  >
                    <option value="nacional">Nacional</option>
                    <option value="regional">Regional CBB</option>
                  </select>
                </div>
                <button
                  type="button"
                  className={styles.btnAddHoliday}
                  onClick={handleAddHoliday}
                >
                  + Agregar
                </button>
              </div>
            </>
          )}
        </div>

        {/* Footer */}
        <div className={styles.footer}>
          <button
            type="button"
            className={styles.btnCancel}
            onClick={onClose}
            disabled={isSaving}
          >
            Cancelar
          </button>
          <button
            type="button"
            className={styles.btnSave}
            onClick={handleSave}
            disabled={isSaving}
          >
            <Icon name="check" />
            <span>{isSaving ? 'Guardando...' : 'Guardar Horarios y Feriados'}</span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default memo(ScheduleEditorModal);
